// packages/chordpro/src/section-name.js
//
// Нормализация имени раздела в «семью + номер». На этом стоит слой связи
// заголовок↔секция: [CHORUS] должен находить Chorus-1 и Chorus-2, а PRE CHORUS
// и Pre-Chorus — одно и то же.
//
// Словарь семей снят с настоящей библиотеки (94 песни): это НЕ полный список
// музыкальных терминов, а перечень того, что реально встречается в аранжировках
// и в чартах. Незнакомое имя — не ошибка: семьи нет, ключ есть, связь просто
// не найдётся.

const FAMILIES = new Set([
  'intro', 'verse', 'prechorus', 'chorus', 'postchorus', 'bridge', 'interlude',
  'instrumental', 'solo', 'breakdown', 'vamp', 'tag', 'outro', 'ending',
  'countdown', 'countoff', 'countin', 'turnaround', 'hook', 'refrain', 'halfchorus',
]);

const ALIASES = new Map([
  ['inst', 'instrumental'],
  ['instr', 'instrumental'],
]);

/**
 * @param {string|null|undefined} raw
 * @returns {{family:string|null, number:number|null, key:string}}
 */
export function normalizeSectionName(raw) {
  const t = String(raw ?? '').trim();
  const unwrapped = /^[[(](.*)[\])]$/.exec(t);
  let s = (unwrapped ? unwrapped[1] : t)
    .trim()
    .replace(/:+$/, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!s) return { family: null, number: null, key: '' };

  // Номер стоит либо перед именем («1 Verse»), либо после («Verse 1»).
  let number = null;
  let m = /^(\d{1,2})\s+(.+)$/.exec(s);
  if (m) {
    number = Number(m[1]);
    s = m[2];
  } else {
    m = /^(.+?)\s+(\d{1,2})$/.exec(s);
    if (m) {
      s = m[1];
      number = Number(m[2]);
    }
  }

  // «PRE CHORUS» и «Pre-Chorus» сходятся только после склейки пробелов.
  const compact = s.replace(/\s+/g, '');
  const family = FAMILIES.has(compact) ? compact : (ALIASES.get(compact) ?? null);
  const base = family ?? compact;

  return { family, number, key: number == null ? base : `${base}#${number}` };
}

const CHORUS_LIKE = new Set(['chorus', 'halfchorus', 'postchorus', 'refrain']);

/** Грубый класс раздела — для оформления, не для связи. */
export function sectionKind(name) {
  const { family } = normalizeSectionName(name);
  if (family && CHORUS_LIKE.has(family)) return 'chorus';
  return family ?? 'other';
}

// Русские и украинские имена разделов — ТОЛЬКО для распознавания строки как
// заголовка (isHeadingLine). В семьи (FAMILIES/ALIASES) они нарочно не входят:
// normalizeSectionName().key — ключ связи заголовок↔секция, и превращение
// «Припев» в семью chorus поменяло бы ключи уже сохранённых связей.
// Цвета и слова гайдов живут в packages/ui-primitives/src/section-semantics.js.
const LOCAL_SECTION_WORDS = new Set([
  'куплет', 'припев', 'приспів', 'пріспів', 'бридж', 'брідж', 'проигрыш', 'програш',
  'вступление', 'вступ', 'интро', 'інтро', 'кода', 'концовка', 'кінцівка', 'окончание',
  'предприпев', 'передприспів', 'переход', 'перехід', 'инструментал', 'інструментал', 'тег',
]);

/** «Куплет 1», «Приспів», «Бридж:» — заголовок по словарю, а не по двоеточию. */
export function isLocalSectionWord(name) {
  const s = String(name ?? '').trim().replace(/:+$/, '').trim().toLowerCase()
    .replace(/\s+\d{1,2}$/, '').replace(/^\d{1,2}\s+/, '').trim();
  return LOCAL_SECTION_WORDS.has(s);
}
